const NATIVE_OPEN_URL = 'openNativeUrl';
const NATIVE_SET_SAFE_AREA_COLOR = 'setSafeAreaColor';
const NATIVE_ACCEPT_PUSH_NOTIFICATIONS = 'handleAcceptIosPushNative';
const NATIVE_DIRECT_TO_SETTINGS = 'directToSettings';
const NATIVE_NOTIFICATIONS_ENABLED = 'notificationsEnabled';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const NATIVE_SHOW_FIRST_PUSH_PERMISSIONS_PROMPT =
    'shouldShowModalFullScreenPushPrompt';
const NATIVE_SHOW_SECOND_PUSH_PERMISSIONS_PROMPT = 'shouldShowPushPrompt';
const NATIVE_ASK_FOR_NATIVE_APP_RATING = 'askForRating';

interface GenericNativeFunction {
    (...args: any[]): void;
}

interface GetNativeFunction<C = GenericNativeFunction> {
    /** If the native function actually exists */
    exists: boolean;
    /**
     * Call the native function or the fallback
     * default function if it doesn't exist
     */
    call: C;
}

/**
 * Get a function to interface with the native Solv app.
 *
 * Returns an object with two properties, the .call property contains
 * the window function if it exists, else an empty object. The .exists
 * property allows you to easily check that the function was called.
 * @param functionName name of function to retrieve
 * @returns existsAndCallObject a call and an exists property
 */
export const getNativeFunction = <C = GenericNativeFunction>(
    functionName: any,
): GetNativeFunction<C> => {
    const exists =
        typeof window !== 'undefined' && typeof window[functionName] === 'function';
    return {
        call: (exists ? window[functionName] : () => ({})) as unknown as C,
        exists,
    };
};

export const getNativeFlag = (flagName: any): boolean => {
    const exists =
        typeof window !== 'undefined' && typeof window[flagName] === 'boolean';
    return exists ? (window[flagName] as unknown as boolean) : false;
};

/**
 * Helper class to support the growing list of native functions.
 *
 * Typed out following the function signatures of the
 * `webviewInjectedJavascript.ts` file inside of mapp-native repo.
 */
export class NativeFunctions {
    /**
     * Check if the current app is the native app.
     * @returns true if the current app is the native app, false otherwise.
     */
    static get isNativeApp() {
        return this.openNativeUrl.exists;
    }

    /**
     * Open a URL in the native browser.
     * @returns a wrapped native function that can open a URL in the native browser.
     */
    static get openNativeUrl() {
        return getNativeFunction<(url: string) => void>(NATIVE_OPEN_URL);
    }

    /**
     * Set the safe area/title bar color on the native app.
     * @returns a wrapped native function that can set the safe area/title bar color.
     */
    static get setSafeAreaColor() {
        return getNativeFunction<(color: string) => void>(
            NATIVE_SET_SAFE_AREA_COLOR,
        );
    }

    /**
     * Handle the user accepting push notifications on iOS OR ANDROID (poorly named old function).
     * @returns a wrapped native function that can handle the user accepting push notifications.
     */
    static get handleAcceptIosPushNative() {
        return getNativeFunction<(origin?: string) => void>(
            NATIVE_ACCEPT_PUSH_NOTIFICATIONS,
        );
    }

    /**
     * Direct the user to the settings page on native.
     * @returns a wrapped native function that can direct the user to the settings page.
     */
    static get directToSettings() {
        return getNativeFunction<() => void>(NATIVE_DIRECT_TO_SETTINGS);
    }

    static get askForNativeAppRating() {
        return getNativeFunction<() => void>(NATIVE_ASK_FOR_NATIVE_APP_RATING);
    }
}

export class NativeFlags {
    static get notificationsEnabled() {
        return getNativeFlag(NATIVE_NOTIFICATIONS_ENABLED);
    }

    static get shouldShowInitialPushNotificationsPermissionsPrompt() {
        return getNativeFlag(NATIVE_SHOW_FIRST_PUSH_PERMISSIONS_PROMPT);
    }

    static get shouldShowFollowUpPushNotificationsPermissionsPrompt() {
        return getNativeFlag(NATIVE_SHOW_SECOND_PUSH_PERMISSIONS_PROMPT);
    }
}